import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { MenuIcon } from 'lucide-react';
import { WhatsAppIcon } from './components/icons/WhatsAppIcon';
import { Reveal } from './components/Reveal';
import { Hero } from './components/Hero';
import { ServicesSection } from './components/ServicesSection';
import { ServiceSheet } from './components/ServiceSheet';
import { ShareSheet } from './components/ShareSheet';
import { ContactSheet } from './components/ContactSheet';
import { LocationSection } from './components/LocationSection';
import { SocialSection } from './components/SocialSection';
import { Toast } from './components/Toast';
import { business } from './data/business';
import { whatsappLink } from './utils/whatsapp';
import { aplicarPreviewSocial } from './utils/socialPreview';
import type { Service } from './types/business';
interface AppProps {
  showPrecos?: boolean;
}
export function App({
  showPrecos = true
}: AppProps) {
  const [servicoAtivo, setServicoAtivo] = useState<Service | null>(null);
  const [compartilharAberto, setCompartilharAberto] = useState(false);
  const [contatoAberto, setContatoAberto] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const timeout = useRef<number | undefined>(undefined);
  const notificar = useCallback((mensagem: string) => {
    setToast(mensagem);
    window.clearTimeout(timeout.current);
    timeout.current = window.setTimeout(() => setToast(null), 2200);
  }, []);
  useEffect(() => () => window.clearTimeout(timeout.current), []);
  useEffect(() => {
    aplicarPreviewSocial(business);
  }, []);
  return <div className="flex min-h-full w-full justify-center bg-shell" data-id="element-0" data-mp="38k6u2msedhir">
      <div className="relative w-full max-w-[460px] bg-cream shadow-card" data-id="element-1" data-mp="3dl4lzr37lpxg">
        <Hero onCompartilhar={() => setCompartilharAberto(true)} data-id="element-2" data-mp="nvj1lae5u4n3" />

        <main data-id="element-3" data-mp="nvanjf6awtab">
          <Reveal data-id="element-4" data-mp="nxwvhfx3tat2">
            <ServicesSection onSelecionar={setServicoAtivo} data-id="element-5" data-mp="sudlkfnv5z8s" />
          </Reveal>
          <Reveal data-id="element-6" data-mp="swgm3tmn7arr">
            <LocationSection data-id="element-7" data-mp="3688dot76uy6" />
          </Reveal>
          <Reveal data-id="element-8" data-mp="34x4zq3nzp7l">
            <SocialSection data-id="element-9" data-mp="38ktgau95lfg" />
          </Reveal>
        </main>

        <footer className="mt-14 bg-ink px-4 pb-28 pt-10 text-center" data-id="element-10" data-mp="3ubzsa6gdtbcj">
          <p className="font-display text-[24px] text-cream" data-id="element-11" data-mp="d57ep0qhqei0">{business.nome}</p>
          <p className="mt-1 text-[12px] text-cream/70" data-id="element-12" data-mp="85l12q6r2w8o">
            {business.especialidade} · {business.cidade}
          </p>
          <p className="mt-5 text-[11px] text-cream/55" data-id="element-13" data-mp="3gklbm69y5m8n">
            © {new Date().getFullYear()} {business.nome}. Todos os direitos reservados.
          </p>
        </footer>

        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1],
        delay: 0.25
      }} className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-3" data-id="element-14" data-mp="3qaxs9nen4v9i">
          <div className="pointer-events-auto flex w-full max-w-[434px] items-center gap-2 rounded-2xl border border-shell bg-cream/95 p-2 shadow-bar backdrop-blur" style={{
          paddingTop: "6px",
          paddingBottom: "6px",
          paddingLeft: "6px",
          paddingRight: "6px",
          gap: "6px"
        }} data-id="element-15" data-mp="2svtr812lm86f">
            <a href={whatsappLink(business.whatsapp, `Olá, ${business.profissional}! Vim pela sua página e quero agendar uma avaliação.`)} target="_blank" rel="noreferrer" className="flex h-[46px] flex-1 items-center justify-center gap-2 rounded-xl bg-ink text-[14px] font-medium text-cream transition-[background-color,transform] duration-150 ease-out hover:bg-[#3f352c] active:scale-[0.985] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mocha" data-id="element-16" data-mp="yfljtkz8ulc4">
              <WhatsAppIcon className="h-[18px] w-[18px]" data-id="element-17" data-mp="1m561m0jnvcjt" />
              Agendar avaliação
            </a>
            <button type="button" onClick={() => setCompartilharAberto(true)} aria-label="Mais opções: compartilhar, QR Code e salvar contato" className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl border border-shell bg-white/70 text-ink transition-colors duration-150 ease-out hover:border-mocha/30 hover:bg-white active:bg-sand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mocha" data-id="element-18" data-mp="6onpkch8asoj">
              <MenuIcon className="h-5 w-5 text-mocha" strokeWidth={1.8} data-id="element-19" data-mp="h59h2k0rks4o" />
            </button>
          </div>
        </motion.div>

        <ServiceSheet servico={servicoAtivo} showPrecos={showPrecos} onClose={() => setServicoAtivo(null)} data-id="element-20" data-mp="19vrjditnyph1" />
        <ShareSheet open={compartilharAberto} onClose={() => setCompartilharAberto(false)} onSalvarContato={() => {
        setCompartilharAberto(false);
        setContatoAberto(true);
      }} onToast={notificar} data-id="element-21" data-mp="14xg8r5vjoaxr" />
        <ContactSheet open={contatoAberto} onClose={() => setContatoAberto(false)} onToast={notificar} data-id="element-22" data-mp="3kob3mdmjo24r" />
        <Toast message={toast} data-id="element-23" data-mp="3gok7rvi5uvtd" />
      </div>
    </div>;
}